import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { PieChart } from 'react-minimal-pie-chart'
import SummaryStatItem from './SummaryStatItem'
import { RISK_TYPE } from '../ultils/chart-d3-utils'

const useStyles = makeStyles(theme => {
  return {
    typeContainer: {
      padding: '23px 20px 28px 20px',
      width: 200,
    },
    pieChart: {
      position: 'relative',
      width: 200,
    },
    pieChartOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: 200,
      height: 200,
      pointerEvents: 'none',
    },
    innerBox: {
      position: 'absolute',
      top: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '200px',
      width: '200px',
      pointerEvents: 'none',
    },
    titleText: {
      color: '#181818',
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '18px',
      letterSpacing: '0em',
      position: 'relative',
      top: 6,
    },
    numberText: {
      color: '#181818',
      fontSize: '32px',
      fontWeight: 600,
      lineHeight: '38px',
      letterSpacing: '0em',
    },
    companiesText: {
      color: '#757575',
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '22px',
      position: 'relative',
      top: -6,
    },
  }
})

interface SummaryStatsBoxProps {
  total: number
  data: any
  riskType: any
  setMarblesData?: any
  segmentClicked: any
  setSegmentClicked: Function
}

const SummaryStatsBox = ({
  total,
  data,
  riskType,
  setMarblesData,
  segmentClicked,
  setSegmentClicked,
}: SummaryStatsBoxProps) => {
  const classes = useStyles()
  const [activeSegment, setActiveSegment]: any = useState(data[0])
  useEffect(() => {
    setActiveSegment(data[0])
  }, [data])

  const activeKey = segmentClicked?.title || activeSegment?.title
  const currentActiveSegment =
    data.find((d: any) => d?.title === activeKey) || activeSegment || data[0]

  const getNumberOfRiskCount = () => {
    if (riskType === RISK_TYPE.CREDIT) {
      return (
        currentActiveSegment?.creditRiskNumber ?? currentActiveSegment.count
      )
    }
    return currentActiveSegment?.equityRiskNumber ?? currentActiveSegment.count
  }
  const isActiveIndex = (i: number) => data[i]?.title === activeKey

  const outlineWidthPx = 3
  const baseRadius = 90
  const overlayPaddingAngle = 1.3

  const totalValue = data.reduce(
    (sum: number, d: any) => sum + (Number(d?.value) || 0),
    0,
  )
  const activeIndex = Math.max(
    0,
    data.findIndex((d: any) => d?.title === activeKey),
  )
  const baseTotalDegrees = 360

  const getSegmentDegrees = (index: number, degreesTotal: number) => {
    if (!totalValue || !data[index]) return 0
    return (Number(data[index].value) / totalValue) * degreesTotal
  }

  const getSegmentStartAngle = (
    index: number,
    degreesTotal: number,
    paddingAngle: number,
  ) => {
    if (!totalValue) return 0
    let start = 0
    for (let i = 0; i < index; i += 1) {
      start += (Number(data[i].value) / totalValue) * degreesTotal
      start += paddingAngle
    }
    return start
  }

  const baseStart = getSegmentStartAngle(activeIndex, baseTotalDegrees, 0)
  const baseDegrees = getSegmentDegrees(activeIndex, baseTotalDegrees)
  const baseCenter = baseStart + baseDegrees / 2

  const centerX = 100
  const centerY = 100
  const shiftDistance = 0
  const toRadians = (deg: number) => (deg * Math.PI) / 180
  const shiftRad = toRadians(baseCenter)
  const shiftDx = shiftDistance * Math.cos(shiftRad)
  const shiftDy = shiftDistance * Math.sin(shiftRad)

  const outlineTrimAngle = overlayPaddingAngle
  const outlineCapAngle =
    (outlineWidthPx / 2 / (baseRadius + outlineWidthPx * 1.5)) * (180 / Math.PI)
  const outlineStartAngle = baseStart + outlineTrimAngle + outlineCapAngle
  const outlineEndAngle =
    baseStart + baseDegrees - outlineTrimAngle - outlineCapAngle
  const outlineHasRoom = outlineEndAngle > outlineStartAngle

  const whiteOutlineRadius = baseRadius + outlineWidthPx / 2
  const colorOutlineRadius = baseRadius + outlineWidthPx * 1.5

  const buildArcPath = (
    radius: number,
    startAngle: number,
    endAngle: number,
  ) => {
    const startRad = toRadians(startAngle)
    const endRad = toRadians(endAngle)
    const x1 = centerX + shiftDx + radius * Math.cos(startRad)
    const y1 = centerY + shiftDy + radius * Math.sin(startRad)
    const x2 = centerX + shiftDx + radius * Math.cos(endRad)
    const y2 = centerY + shiftDy + radius * Math.sin(endRad)
    const largeArc = endAngle - startAngle > 180 ? 1 : 0
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`
  }

  const connectorInnerRadius = baseRadius
  const connectorOuterRadius = colorOutlineRadius
  const connectorStartInner = outlineStartAngle
  const connectorStartOuter = outlineStartAngle
  const connectorEndInner = outlineEndAngle
  const connectorEndOuter = outlineEndAngle

  const buildConnectorPath = (innerAngle: number, outerAngle: number) => {
    const innerRad = toRadians(innerAngle)
    const outerRad = toRadians(outerAngle)
    const x1 = centerX + shiftDx + connectorInnerRadius * Math.cos(innerRad)
    const y1 = centerY + shiftDy + connectorInnerRadius * Math.sin(innerRad)
    const x2 = centerX + shiftDx + connectorOuterRadius * Math.cos(outerRad)
    const y2 = centerY + shiftDy + connectorOuterRadius * Math.sin(outerRad)
    const controlAngle = (innerAngle + outerAngle) / 2
    const controlRadius = (connectorInnerRadius + connectorOuterRadius) / 2
    const cx =
      centerX + shiftDx + controlRadius * Math.cos(toRadians(controlAngle))
    const cy =
      centerY + shiftDy + controlRadius * Math.sin(toRadians(controlAngle))
    return `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`
  }
  return (
    <>
      <div className={classes.pieChart}>
        <PieChart
          animate
          animationDuration={500}
          animationEasing="ease-out"
          lineWidth={35}
          radius={90}
          viewBoxSize={[200, 200]}
          center={[100, 100]}
          onMouseOver={(_, i) => setActiveSegment(data[i])}
          onMouseOut={() => setActiveSegment(data[0])}
          onClick={(_, i) => {
            if (segmentClicked?.index === i) {
              setMarblesData(null)
              setSegmentClicked(null)
            } else {
              setMarblesData(data[i])
              setSegmentClicked(data[i])
            }
          }}
          segmentsStyle={{ transition: 'all 0.2s', cursor: 'pointer' }}
          data={data}
        />
        <svg className={classes.pieChartOverlay} viewBox="0 0 200 200">
          {isActiveIndex(activeIndex) && totalValue > 0 && outlineHasRoom ? (
            <>
              <path
                d={buildArcPath(
                  whiteOutlineRadius,
                  outlineStartAngle,
                  outlineEndAngle,
                )}
                stroke="#FFFFFF"
                strokeWidth={outlineWidthPx}
                strokeLinecap="round"
                fill="none"
              />
              <path
                d={buildArcPath(
                  colorOutlineRadius,
                  outlineStartAngle,
                  outlineEndAngle,
                )}
                stroke={data[activeIndex]?.color}
                strokeWidth={outlineWidthPx}
                strokeLinecap="round"
                fill="none"
              />
              {[
                [connectorStartInner, connectorStartOuter],
                [connectorEndInner, connectorEndOuter],
              ].map(([innerAngle, outerAngle], index) => (
                <path
                  key={`connector-${index}`}
                  d={buildConnectorPath(innerAngle, outerAngle)}
                  stroke={data[activeIndex]?.color}
                  strokeWidth={outlineWidthPx}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              ))}
            </>
          ) : null}
        </svg>
        <div className={classes.innerBox}>
          <div
            className={classes.titleText}
            // style={{ color: segmentClicked?.color || activeSegment.color }}
          >
            {segmentClicked?.title || activeSegment.title}
          </div>
          <div className={classes.numberText}>{getNumberOfRiskCount()}</div>
          <div className={classes.companiesText}>Companies</div>
        </div>
      </div>
      <div className={classes.typeContainer}>
        {data
          .filter((d: any) => d.title !== 'Total')
          .map((d: any) => (
            <SummaryStatItem d={d} total={total} key={d.title} />
          ))}
      </div>
    </>
  )
}

export default SummaryStatsBox
